import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, ArrowLeft, ArrowRight, Lock as LockIcon, HelpCircle } from 'lucide-react';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import MainLayout from '../../Layout/MainLayout';

const FEATURE_META = {
  'create-voucher': {
    title: 'Voucher Creation',
    requiredPlan: 'Starter'
  },
  'live-location': {
    title: 'Live Location Tracking',
    requiredPlan: 'Pro'
  },
  'flight-tracking': {
    title: 'Flight Tracking',
    requiredPlan: 'Pro'
  },
  'feedback': {
    title: 'Customer Feedback',
    requiredPlan: 'Pro'
  },
  'chat': {
    title: 'In-App Traveller Chat',
    requiredPlan: 'Starter'
  },
  'post-connect': {
    title: 'Post Trip Connect',
    requiredPlan: 'Growth'
  },
  'packages': {
    title: 'Post Trip Packages',
    requiredPlan: 'Pro'
  },
  'enquiries': {
    title: 'Post Trip Enquiries',
    requiredPlan: 'Growth'
  },
  'notifications': {
    title: 'Post Trip Notifications',
    requiredPlan: 'Growth'
  },
  'partners': {
    title: 'Partner Management',
    requiredPlan: 'Growth'
  },
  'role-management': {
    title: 'Role & Team Management',
    requiredPlan: 'Enterprise'
  },
  'roles': {
    title: 'Role & Team Management',
    requiredPlan: 'Enterprise'
  }
};

const FeatureLocked = ({ featureKey = 'live-location', standalone = true, overlay = false }) => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const cardRef = useRef(null);

  useEffect(() => {
    if (overlay && cardRef.current) {
      const scrollToCenter = () => {
        if (!cardRef.current) return;
        const mainContainer = cardRef.current.closest('main') || document.querySelector('main');
        if (mainContainer && typeof mainContainer.scrollTo === 'function') {
          const cardRect = cardRef.current.getBoundingClientRect();
          const mainRect = mainContainer.getBoundingClientRect();
          const currentScroll = mainContainer.scrollTop;
          const targetScroll = currentScroll + (cardRect.top - mainRect.top) - (mainRect.height / 2) + (cardRect.height / 2);
          
          mainContainer.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth'
          });
        } else {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };

      const t1 = setTimeout(scrollToCenter, 100);
      const t2 = setTimeout(scrollToCenter, 300);
      const t3 = setTimeout(scrollToCenter, 600);

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
  }, [overlay]);

  const currentPlanName = subscription?.plan_id?.name || (subscription?.status === 'trial' ? 'Trial Period - Growth' : 'Starter');
  const meta = FEATURE_META[featureKey] || { title: 'Premium Feature', requiredPlan: 'Pro' };

  const cardContent = (
    <div ref={cardRef} className="w-full max-w-md bg-white rounded-3xl border border-slate-200/90 shadow-[0_20px_50px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.05)] p-6 sm:p-7 text-center animate-in fade-in zoom-in-95 duration-200 font-sans">
      
      {/* Centered Soft Golden Crown Icon */}
      <div className="w-11 h-11 mx-auto mb-3 rounded-2xl bg-amber-50/90 border border-amber-200/60 flex items-center justify-center text-amber-500 shadow-2xs">
        <Crown size={22} className="fill-amber-400 text-amber-500" />
      </div>

      {/* Status Pill */}
      <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-teal-50/80 border border-teal-200/60 text-[11px] font-normal text-[#067978] mb-2.5">
        <LockIcon size={11} className="text-[#067978]" />
        <span>Requires {meta.requiredPlan} Plan</span>
      </div>

      {/* Heading */}
      <h2 className="text-lg sm:text-xl font-normal text-slate-800 tracking-tight mb-2">
        {meta.title}
      </h2>

      {/* Simple, direct explanation without bold texts */}
      <p className="text-slate-500 text-xs sm:text-[13px] font-normal leading-relaxed mb-5 max-w-sm mx-auto">
        Your current plan ({currentPlanName}) does not include {meta.title}. Upgrade to the {meta.requiredPlan} Plan to unlock this and other features.
      </p>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full max-w-sm mx-auto">
        <button
          onClick={() => navigate('/subscription')}
          className="w-full sm:flex-1 py-3 px-5 bg-[#067978] hover:bg-[#056362] text-white font-normal text-xs sm:text-sm rounded-2xl shadow-sm shadow-[#067978]/20 transition-all hover:shadow-md active:scale-95 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <Crown size={15} className="text-amber-300 fill-amber-300 shrink-0" />
          <span>Upgrade to {meta.requiredPlan} Plan</span>
          <ArrowRight size={14} className="shrink-0" />
        </button>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full sm:w-auto py-3 px-4 bg-white hover:bg-slate-50 text-slate-600 border border-slate-200 font-normal text-xs sm:text-sm rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs whitespace-nowrap"
        >
          <ArrowLeft size={14} className="text-slate-400 shrink-0" />
          <span>Dashboard</span>
        </button>
      </div>

      {!overlay && (
        <div className="mt-5 pt-4 border-t border-slate-100 text-center">
          <button 
            onClick={() => navigate('/support')}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 font-normal hover:text-[#067978] transition-colors cursor-pointer"
          >
            <HelpCircle size={12} className="text-slate-400" />
            <span>Need help? Contact Support</span>
          </button>
        </div>
      )}

    </div>
  );

  if (overlay) {
    return (
      <div className="absolute inset-0 z-30 flex items-center justify-center p-4 backdrop-blur-[1px] bg-slate-950/10 rounded-2xl animate-in fade-in duration-200 overflow-hidden">
        {cardContent}
      </div>
    );
  }

  const content = (
    <div className={`flex flex-col justify-center items-center px-4 py-8 max-w-xl mx-auto font-sans ${standalone ? 'min-h-[calc(100vh-140px)]' : 'w-full'}`}>
      {cardContent}
    </div>
  );

  if (!standalone) {
    return content;
  }

  return (
    <MainLayout activeTab="dashboard">
      {content}
    </MainLayout>
  );
};

export default FeatureLocked;
